import * as Config from '@/Config';

/**
 * Meta tag management utility for dynamically updating page metadata
 * including title, description, Open Graph tags, and other SEO-related meta tags.
 *
 * @author Matt Kenefick <matt@polymermallard.com>
 * @package Manager
 * @project ChalkySticks
 */
export default class Meta {
	/**
	 * How many requests to update metadata
	 *
	 * @type number
	 */
	protected static updateAttempts: number = 0;

	/**
	 * Update multiple meta tags at once
	 *
	 * @param object metaData
	 * @return void
	 */
	public static updateMeta(metaData: {
		author?: string;
		canonical?: string;
		description?: string;
		image?: string;
		imageHeight?: number;
		imageWidth?: number;
		keywords?: string;
		ogSiteName?: string;
		ogType?: string;
		robots?: string;
		title?: string;
	}): void {
		this.updateAttempts++;

		if (!Config.App.USE_DYNAMIC_META && this.updateAttempts <= 1) {
			return;
		}

		if (metaData.title) {
			this.setTitle(metaData.title);
		}

		if (metaData.description) {
			this.setDescription(metaData.description);
		}

		if (metaData.canonical) {
			this.setCanonicalUrl(metaData.canonical);
		}

		if (metaData.image) {
			this.setOpenGraphImage(metaData.image, metaData.imageWidth, metaData.imageHeight);
		}

		if (metaData.keywords) {
			this.setKeywords(metaData.keywords);
		}

		if (metaData.ogType) {
			this.setOpenGraphType(metaData.ogType);
		}

		if (metaData.ogSiteName) {
			this.setOpenGraphSiteName(metaData.ogSiteName);
		}

		if (metaData.robots) {
			this.setRobots(metaData.robots);
		}

		if (metaData.author) {
			this.setMetaName('author', metaData.author);
		}
	}

	/**
	 * Set the page title
	 *
	 * @param string title
	 * @return void
	 */
	public static setTitle(title: string): void {
		document.title = title;

		// Update Open Graph title
		this.setMetaProperty('og:title', title);
	}

	/**
	 * Set the page description and Open Graph description
	 *
	 * @param string description
	 * @return void
	 */
	public static setDescription(description: string): void {
		this.setMetaName('description', description);
		this.setMetaProperty('og:description', description);
	}

	/**
	 * Set the canonical URL and Open Graph URL
	 *
	 * @param string url
	 * @return void
	 */
	public static setCanonicalUrl(url: string): void {
		// Update canonical link
		let canonicalLink: HTMLLinkElement | null = document.querySelector('link[rel="canonical"]');
		if (!canonicalLink) {
			canonicalLink = document.createElement('link');
			canonicalLink.rel = 'canonical';
			document.head.appendChild(canonicalLink);
		}
		canonicalLink.href = url;

		// Update Open Graph URL
		this.setMetaProperty('og:url', url);
	}

	/**
	 * Set the Open Graph image
	 *
	 * @param string imageUrl
	 * @param number width
	 * @param number height
	 * @return void
	 */
	public static setOpenGraphImage(imageUrl: string, width?: number, height?: number): void {
		this.setMetaProperty('og:image', imageUrl);

		if (width) {
			this.setMetaProperty('og:image:width', width.toString());
		}

		if (height) {
			this.setMetaProperty('og:image:height', height.toString());
		}
	}

	/**
	 * Set meta keywords
	 *
	 * @param string keywords
	 * @return void
	 */
	public static setKeywords(keywords: string): void {
		this.setMetaName('keywords', keywords);
	}

	/**
	 * Set the Open Graph type (website, article, etc.)
	 *
	 * @param string type
	 * @return void
	 */
	public static setOpenGraphType(type: string): void {
		this.setMetaProperty('og:type', type);
	}

	/**
	 * Set the Open Graph site name
	 *
	 * @param string siteName
	 * @return void
	 */
	public static setOpenGraphSiteName(siteName: string): void {
		this.setMetaProperty('og:site_name', siteName);
	}

	/**
	 * Set robots meta tag
	 *
	 * @param string content
	 * @return void
	 */
	public static setRobots(content: string): void {
		this.setMetaName('robots', content);
	}

	/**
	 * Set a generic meta tag by name attribute
	 *
	 * @param string name
	 * @param string content
	 * @return void
	 */
	public static setMetaName(name: string, content: string): void {
		let metaTag: HTMLMetaElement | null = document.querySelector(`meta[name="${name}"]`);

		if (!metaTag) {
			metaTag = document.createElement('meta');
			metaTag.name = name;
			document.head.appendChild(metaTag);
		}

		metaTag.content = content;
	}

	/**
	 * Set a generic meta tag by property attribute (for Open Graph tags)
	 *
	 * @param string property
	 * @param string content
	 * @return void
	 */
	public static setMetaProperty(property: string, content: string): void {
		let metaTag: HTMLMetaElement | null = document.querySelector(`meta[property="${property}"]`);

		if (!metaTag) {
			metaTag = document.createElement('meta');
			metaTag.setAttribute('property', property);
			document.head.appendChild(metaTag);
		}

		metaTag.content = content;
	}

	/**
	 * Reset meta tags to default values from the original HTML
	 *
	 * @return void
	 */
	public static resetToDefaults(): void {
		this.updateMeta({
			author: 'ChalkySticks',
			canonical: 'https://www.chalkysticks.com',
			description:
				'ChalkySticks is the ultimate pool and billiards app. Find your felt anywhere in the world — discover pool halls, watch live billiards, improve your game, and connect with players worldwide.',
			image: 'https://www.chalkysticks.com/image/graphic/app-share.jpg',
			imageHeight: 630,
			imageWidth: 1200,
			keywords: 'pool tables, billiards, cue sports, 8-ball, 9-ball, snooker, pool halls, bar pool, APA league, NYC pool, ChalkySticks app',
			ogSiteName: 'ChalkySticks',
			ogType: 'website',
			robots: 'index, follow',
			title: 'ChalkySticks – Find Your Felt',
		});
	}
}
